/**
 * Persist Expand / Pin chrome for an operator workspace window (session).
 * Expand and pin are independent: pinned can be compact or near-fullscreen.
 * Optional leftPct/topPct for a dragged pinned window.
 */

/**
 * @param {string} storageKey
 */
export function createOperatorWorkspaceChrome(storageKey) {
    function readChrome() {
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (!raw) {
                return { expanded: false, pinned: false, leftPct: null, topPct: null };
            }
            const parsed = JSON.parse(raw);
            const leftPct =
                typeof parsed?.leftPct === 'number' && Number.isFinite(parsed.leftPct)
                    ? parsed.leftPct
                    : null;
            const topPct =
                typeof parsed?.topPct === 'number' && Number.isFinite(parsed.topPct)
                    ? parsed.topPct
                    : null;
            return {
                expanded: Boolean(parsed?.expanded),
                pinned: Boolean(parsed?.pinned),
                leftPct,
                topPct,
            };
        } catch {
            return { expanded: false, pinned: false, leftPct: null, topPct: null };
        }
    }

    /**
     * @param {{ expanded?: boolean, pinned?: boolean, leftPct?: number|null, topPct?: number|null }} next
     */
    function writeChrome(next) {
        try {
            const cur = readChrome();
            const pinned = next.pinned != null ? Boolean(next.pinned) : cur.pinned;
            const expanded = next.expanded != null ? Boolean(next.expanded) : cur.expanded;
            const leftPct =
                next.leftPct !== undefined
                    ? next.leftPct == null
                        ? null
                        : Number(next.leftPct)
                    : cur.leftPct;
            const topPct =
                next.topPct !== undefined
                    ? next.topPct == null
                        ? null
                        : Number(next.topPct)
                    : cur.topPct;
            sessionStorage.setItem(
                storageKey,
                JSON.stringify({
                    expanded,
                    pinned,
                    leftPct: leftPct != null && Number.isFinite(leftPct) ? leftPct : null,
                    topPct: topPct != null && Number.isFinite(topPct) ? topPct : null,
                })
            );
        } catch {
            /* ignore */
        }
    }

    function readPinnedPosition() {
        const cur = readChrome();
        if (cur.leftPct == null || cur.topPct == null) return null;
        return { leftPct: cur.leftPct, topPct: cur.topPct };
    }

    /**
     * @param {{ leftPct: number, topPct: number }|null} pos
     */
    function writePinnedPosition(pos) {
        if (!pos) {
            writeChrome({ leftPct: null, topPct: null });
            return null;
        }
        writeChrome({ leftPct: pos.leftPct, topPct: pos.topPct });
        return pos;
    }

    return {
        readChrome,
        writeChrome,
        readPinnedPosition,
        writePinnedPosition,
    };
}
